<template>
  <main>
    <div class="max-w-1200 hero md:rounded overflow-hidden">
      <section class="hero-header-container relative md:mb-10">
        <OImage
          v-bind="headerImage"
          img-class="hero-header md:rounded w-full"
          :alt="$t('Accessibilty.AltTags.WomenProductImage')"
        />
        <div class="text-neutral-800 p-5 md:pl-10 max-w-408">
          <h1 class="o-heading-1 mb-3" v-text="$t('LandingsPage.Women.Title', variables)" />
          <p class="o-body mb-3" v-html="$t('LandingsPage.Women.Description', variables)" />
          <NewButton tag="nuxt-link" to="/women/signup" icon-right="new-chevron-right" color="yellow">
            {{ $t('LandingsPage.Women.TryForFree', variables) }}
          </NewButton>
          <p class="o-body mt-3" v-html="$t('LandingsPage.Women.ACMDescription', variables)" />
        </div>
      </section>
      <div class="px-4 py-10 md:flex md:my-10 md:p-0">
        <div class="md:flex-1 md:mr-9 md:bg-women-shade md:rounded md:flex md:flex-col md:justify-center md:items-center md:text-center">
          <h2 class="text-women-text o-heading-2 md:px-10" v-text="$t('LandingsPage.Women.EveryMonthANewSurprise', variables)" />
          <DontWorryUSPS class="my-3 text-women-text md:px-10" :usps="heroUSPS" />
          <div class="md:hidden">
            <NewButton tag="nuxt-link" to="/women/signup" icon-right="new-chevron-right" color="yellow">
              {{ $t('LandingsPage.Women.StartNow', variables) }}
            </NewButton>
          </div>
          <div class="hidden md:inline-block">
            <NewButton tag="nuxt-link" to="/women/signup" icon-right="new-chevron-right" color="white">
              {{ $t('LandingsPage.Women.StartNow', variables) }}
            </NewButton>
          </div>
        </div>
        <div class="mt-6 md:flex-1 md:mt-0">
          <OImage
            v-bind="videoPlaceholderImage"
            img-class="rounded overflow-hidden w-full object-cover"
            :alt="$t('Accessibilty.AltTags.WomenProductImage')"
          />
        </div>
      </div>
    </div>
    <div id="how-it-works" />
    <WomenHowItWorks
      ref="howItWorks"
      :title="$t('LandingsPage.Women.Every3MonthsFreeTop.Title', variables)"
      :description="$t('LandingsPage.Women.Every3MonthsFreeTop.Description', variables)"
      :atc-text="$t('LandingsPage.Women.PreOrderNow', variables)"
      :how-it-works-slides="howItWorksSlides"
    />
    <section class="max-w-1200">
      <article
        v-for="(feature, i) in features"
        :key="feature.title"
        class="py-10 px-4 md:pt-20 md:pb-0 md:flex"
        :class="{
          'md:flex-row-reverse': i % 2 !== 0,
          'md:pb-20': i + 1 === features.length
        }"
      >
        <div
          class="left-flex"
          :class="{ 'md:flex md:justify-end': i % 2 !== 0}"
        >
          <OImage v-bind="feature.image" img-class="rounded" :alt="$t('Accessibilty.AltTags.USPWomenProductImage')" />
        </div>
        <div
          class="right-flex md:flex md:flex-col md:justify-center"
        >
          <p class="o-heading-3 text-neutral-800 py-5" v-text="feature.title" />
          <p class="o-body" v-text="feature.description" />
        </div>
      </article>
    </section>
    <div class="bg-women-shade">
      <div class="max-w-600">
        <div class="py-10 px-3 text-women-text text-center">
          <p class="o-heading-2 mb-1" v-text="$t('LandingsPage.Women.FreeSetBlock.Title')" />
          <p class="o-body mb-9" v-html="$t('LandingsPage.Women.FreeSetBlock.Description')" />
          <div class="md:flex md:justify-center">
            <div class="text-center mb-3 md:mr-3">
              <NewButton tag="nuxt-link" to="/women/signup" icon-right="new-chevron-right" color="white">
                {{ $t('LandingsPage.Women.PreOrderNow', variables) }}
              </NewButton>
            </div>
            <div class="text-center">
              <NewButton tag="nuxt-link" to="/women/product" icon-right="new-chevron-right" variant="outline" color="black">
                {{ $t('LandingsPage.Women.FreeSetBlock.Characteristics', variables) }}
              </NewButton>
            </div>
          </div>
        </div>
      </div>
    </div>
    <section class="max-w-667">
      <div class="py-10 text-women-text md:text-center md:py-20">
        <div class="px-4 md:flex md:flex-col md:items-center">
          <h3 class="o-heading-3 mb-3" v-text="$t('LandingsPage.Women.YourLoyaltyProgram.Title', variables)" />
          <p class="o-body mb-3" v-html="$t('LandingsPage.Women.YourLoyaltyProgram.Description', variables)" />
          <DontWorryUSPS class="mb-3 text-women-text text-left" :usps="loyaltyUSPS" />
          <div class="md:text-center">
            <NewButton
              tag="nuxt-link"
              to="/loyalty"
              :aria-label="$t('Accessibilty.AriaLabels.GoToWebshop')"
              icon-right="new-chevron-right"
              variant="outline"
              color="black"
            >
              {{ $t('LandingsPage.Women.ExploreMore', variables) }}
            </NewButton>
          </div>
        </div>
        <div>
          <article
            ref="loyaltyProductsSlider"
            class="mt-6 flex overflow-x-scroll disable-scrollbars horizontal-scroll pr-6 md:pr-0"
            tabindex="0"
            role="region"
            :aria-label="$t('LandingsPage.Women.YourLoyaltyProgram.Title', variables)"
            @keydown.left="scrollLoyaltyProductsLeft"
            @keydown.right="scrollLoyaltyProductsRight"
          >
            <div
              v-for="product in loyaltyProducts"
              :key="product.title"
              class="pl-6 flex-1 horizontal-scroll-center"
            >
              <div class="rounded-t">
                <OImage
                  v-bind="getLoyaltyProductImage(product.i)"
                  img-class="rounded mb-4"
                  :alt="$t('Accessibilty.AltTags.PhotoOfProductWithName', { name: product.title })"
                />
                <p class="text-center o-heading-5 mb-6 text-neutral-800" v-text="product.title" />
                <p class="text-center o-body text-neutral-800" v-text="product.description" />
              </div>
            </div>
          </article>
        </div>
      </div>
    </section>
    <div class="bg-women-shade">
      <StarRating
        v-if="rating"
        class="py-10 md:py-20 text-women-text"
        :title="'TryItNow.ReviewsTitle'"
        :rating="rating"
        :reviews="ratingTotal"
        @click="onRatingClick"
      />
    </div>
    <div class="bg-neutral-50">
      <div class="max-w-1200 py-10">
        <Reviews type="women" />
        <p class="o-body text-center py-5" v-text="$t('HomePage.SelectedFavoriteReviews')" />
      </div>
    </div>
    <div class="max-w-1200">
      <InstagramWidget hide-texts class="mt-10 md:mt-15" />
    </div>
    <div class="max-w-1200 bg-women-shade py-10 lg:py-20 md:flex mt-10 md:mt-20">
      <div class="max-w-600 bg-women-shade relative flex md:py-0 md:flex-1 md:pr-0 md:pl-10">
        <div>
          <div class="model-image-side md:py-5 pr-5 md:pr-6 md:flex md:justify-end">
            <OImage
              v-bind="whyOtaImage"
              img-class="rounded"
            />
          </div>
        </div>
        <div class="relative pr-4 md:pt-5">
          <h4 class="o-heading-3 text-women-text md:whitespace-no-wrap" v-text="$t('LandingsPage.Women.TheseAreThePros')" />
          <p class="new-gray o-body mb-5 mt-2" v-text="$t('LandingsPage.Women.WomenPricePerMonth', variables)" />
          <USPs class="what-you-get-usps" :usps="prosUSPS" />
          <div class="pt-5">
            <NewButton tag="nuxt-link" to="/women/signup" icon-right="new-chevron-right" color="white">
              {{ $t('LandingsPage.Women.PreOrderNow', variables) }}
            </NewButton>
          </div>
        </div>
      </div>
    </div>
    <div class="max-w-56rem my-20 px-3">
      <Faq
        :items="faqItems"
      />
    </div>
    <StickyATC class="md:hidden" :text="$t('HomePage.TryForFreeATC')" @click="signupRedirect" />
  </main>
</template>

<script>
import { addLocalCurrency, getEuros, getImagePaths, serializeParameters } from '@/utils/general'
import OImage from '@/components/OImage'
import StarRating from '@/components/StarRating'
import NewButton from '@/components/NewButton'
import InstagramWidget from '@/components/InstagramWidget'
import Reviews from '@/components/Reviews'
import USPs from '@/components/USPs'
import Faq from '@/components/Faq'
import { TrialDuration } from '@/enums/member'
import { mapActions, mapState } from 'vuex'
import DontWorryUSPS from '@/components/Women/Signup/DontWorryUSPS'
import WomenHowItWorks from '@/components/Women/HowItWorks'
import { Orientations, WomenPrices } from '@/enums/products'
import { Currencies, WomenCountries } from '@/enums/general'
import { GET_GOOGLE_RATING, SIGNUP_GET_LATEST_DESIGN } from '@/utils/store/actionTypes'
import { ROUTER_HASH_UPDATED } from '@/events/eventTypes'
import _ from 'lodash'
import { DeliveryTimes } from '@/enums/shipments'
import { WebsiteCategories, WebsiteCategoryCookie } from '@/enums/app'
import { getCookieDomain } from '@/utils/cookie'
import StickyATC from '@/components/StickyATC'

export default {
  name: 'Women',
  layout: 'homepage',
  components: { OImage, StarRating, NewButton, DontWorryUSPS, Faq, InstagramWidget, Reviews, USPs, StickyATC, WomenHowItWorks },
  async fetch () {
    await Promise.all([
      this.getAndSetGoogleRating(),
      this.getLatestWomenDesign()
    ])
  },
  middleware: ({ route, app, redirect, store: { state: { app: { locale, country } } } }) => {
    const i18Params = `/${locale}-${country}`
    const queryParamsString = serializeParameters(route.query)

    if (!WomenCountries.includes(country)) {
      app.$cookies.set(WebsiteCategoryCookie, WebsiteCategories.men, { expires: new Date(Date.now() + 604800), path: '/', domain: getCookieDomain() })
      return redirect(`${i18Params}/men?${queryParamsString}`)
    }
  },
  data () {
    return {
      rating: null,
      ratingTotal: null,
      observer: null,
      latestDesign: {
        [Orientations.womenbralette]: 'camila',
        [Orientations.womenhipster]: 'dani'
      }
    }
  },
  computed: {
    headerImage () {
      return getImagePaths({
        s3: true,
        path: `/images/design-women/${this.latestDesign[Orientations.womenhipster]}/campaign/${this.latestDesign[Orientations.womenhipster]}-women`
      })
    },
    heroUSPS () {
      const path = 'LandingsPage.Women.HeroUSPS'
      const items = this.$t(path)

      if (!Array.isArray(items)) {
        return []
      }

      return items.map((_, i) => this.$t(`${path}[${i}]`, { ...this.variables }))
    },
    features () {
      const path = 'LandingsPage.Women.Features'
      const items = this.$t(path)

      if (!Array.isArray(items)) {
        return []
      }

      return items.map((_, i) => ({
        image: getImagePaths({
          s3: true,
          path: this.$t(`${path}[${i}].Url`, this.variables),
          noMobile: true
        }),
        title: this.$t(`${path}[${i}].Title`, this.variables),
        description: this.$t(`${path}[${i}].Description`, this.variables)
      }))
    },
    loyaltyUSPS () {
      const path = 'LandingsPage.Women.YourLoyaltyProgram.USPS'
      const items = this.$t(path)

      if (!Array.isArray(items)) {
        return []
      }

      return items.map((_, i) => this.$t(`${path}[${i}]`, { ...this.variables }))
    },
    prosUSPS () {
      const path = 'LandingsPage.Women.ProsUSPS'
      const items = this.$t(path)

      if (!Array.isArray(items)) {
        return []
      }

      return items.map((_, i) => this.$t(`${path}[${i}]`, { ...this.variables }))
    },
    faqItems () {
      const path = 'LandingsPage.Women.FAQ'
      const items = this.$t(path)

      if (!Array.isArray(items)) {
        return []
      }

      return items.map((_, i) => ({
        title: this.$t(`${path}[${i}].title`, this.variables),
        description: this.$t(`${path}[${i}].description`, this.variables)
      }))
    },
    videoPlaceholderImage () {
      return getImagePaths({
        path: `/images/design-women/${this.latestDesign[Orientations.womenhipster]}/homepage/women-video`,
        s3: true
      })
    },
    whyOtaImage () {
      return getImagePaths({
        path: `/images/design-women/${this.latestDesign[Orientations.womenhipster]}/homepage/women-model`,
        s3: true
      })
    },
    loyaltyProducts () {
      const path = 'LandingsPage.Women.YourLoyaltyProgram.Items'
      const items = this.$t(path)

      if (!Array.isArray(items)) {
        return []
      }

      return items.map((_, i) => ({
        i: i + 1,
        title: this.$t(`${path}[${i}].Title`, this.variables),
        description: this.$t(`${path}[${i}].Description`, this.variables)
      }))
    },
    howItWorksSlides () {
      const path = 'LandingsPage.Women.Every3MonthsFreeTop.Items'
      const items = this.$t(path)

      if (!Array.isArray(items)) {
        return []
      }

      return items.map((_, i) => ({
        svg: this.$t(`${path}[${i}].Svg`),
        title: this.$t(`${path}[${i}].Title`, this.variables),
        description: this.$t(`${path}[${i}].Description`, this.variables)
      }))
    },
    variables () {
      return {
        trialDuration: TrialDuration[this.country],
        trialDays: TrialDuration[this.country],
        loyaltyAmount: getEuros(this.country, 10),
        womenPrice: addLocalCurrency(WomenPrices[this.country], this.country, Currencies[this.country]),
        hipsterDesign: this.latestDesign[Orientations.womenhipster],
        earliest: DeliveryTimes[this.country].earliest,
        latest: DeliveryTimes[this.country].latest
      }
    },
    ...mapState('app', {
      country: state => state.country
    })
  },
  created () {
    if (process.client) {
      this.observer = new IntersectionObserver(
        this.onElementObserved,
        {
          root: this.$el,
          threshold: 0.8,
          rootMargin: '0px 0px -50px 0px'
        }
      )
      this.$registerEventListener(ROUTER_HASH_UPDATED.event, this.pageScrollByHash)
    }
  },
  mounted () {
    this.pageScrollByHash()
  },
  beforeDestroy () {
    this.$unregisterEventListener(ROUTER_HASH_UPDATED.event, this.pageScrollByHash)
  },
  methods: {
    onElementObserved (entries) {
      entries.forEach(({ target, isIntersecting }) => {
        if (!isIntersecting) {
          return
        }

        this.observer.unobserve(target)

        this.open = true
      })
    },
    signupRedirect () {
      this.$router.push(this.$i18n.localePath({ name: 'women-signup' }))
    },
    pageScroll (refName, retries = 0) {
      const elem = this.$refs[refName]?.$el
      if (retries > 10) {
        return
      }

      if (!elem || !elem.scrollIntoView || this.loading) {
        setTimeout(() => {
          this.pageScroll(refName, this.loading ? retries + 1 : 0)
        }, 200)
        return
      }

      elem.scrollIntoView({ behavior: 'smooth', block: 'start' })
    },
    pageScrollByHash () {
      if (this.$route.hash) {
        setTimeout(() => {
          this.pageScroll(_.camelCase(this.$route.hash.substring(1)))
        }, 1500)
      }
    },
    onRatingClick () {
      window.open(this.$t('HomePage.GoogleReviewPage'), '_blank', 'noopener')
    },
    getLoyaltyProductImage (i = 1) {
      return getImagePaths({
        s3: true,
        path: `/images/frontend/loyalty/women-${i}`
      })
    },
    async getAndSetGoogleRating () {
      try {
        const { rating, ratingTotal } = await this.getGoogleRating()

        this.rating = rating
        this.ratingTotal = ratingTotal
      } catch (error) {
        this.$ota.general.logError(error)
      }
    },
    async getLatestWomenDesign () {
      try {
        const designs = await this.getLatestDesign({ women: true, men: false, boys: false })

        const bralette = designs.find(design => design.productGroups.find(productGroup => productGroup.reference === Orientations.womenbralette))
        const hipster = designs.find(design => design.productGroups.find(productGroup => productGroup.reference === Orientations.womenhipster))

        if (bralette) {
          this.latestDesign[Orientations.womenbralette] = bralette.slug
        }

        if (hipster) {
          this.latestDesign[Orientations.womenhipster] = hipster.slug
        }
      } catch (error) {
        this.$ota.general.logError(error)
      }
    },
    scrollLoyaltyProductsLeft () {
      if (this.$refs.loyaltyProductsSlider) {
        this.$refs.loyaltyProductsSlider.scrollLeft -= 400 // Adjust scroll amount as needed
      }
    },
    scrollLoyaltyProductsRight () {
      if (this.$refs.loyaltyProductsSlider) {
        this.$refs.loyaltyProductsSlider.scrollLeft += 400 // Adjust scroll amount as needed
      }
    },
    ...mapActions('homepage', {
      getGoogleRating: GET_GOOGLE_RATING
    }),
    ...mapActions('signup', {
      getLatestDesign: SIGNUP_GET_LATEST_DESIGN
    })
  },
  head () {
    return {
      meta: [
        { hid: 'og:title', name: 'og:title', property: 'og:title', content: this.$t('LandingsPage.Women.Meta.Title') },
        { hid: 'og:description', name: 'og:description', property: 'og:description', content: this.$t('LandingsPage.Women.Meta.Description') },
        { hid: 'og:image', name: 'og:image', property: 'og:image', content: `https://onthatass.com/images/design-women/${this.latestDesign[Orientations.womenhipster]}/campaign/${this.latestDesign[Orientations.womenhipster]}-women.webp` }
      ]
    }
  }
}
</script>

<style scoped lang="postcss">
@screen xsplus {
  .oimage >>> .hero-header {
    @apply bg-women-fill;
    min-height: 52.5rem;
  }
}

.sticky-atc >>> button {
  @apply bg-yellow-400 !important;
}

.hero-header-container {
  @apply mx-auto;
  max-width: 50rem;
}

.model-image-side .oimage >>> img {
  max-width: unset;
}

.text-women-black {
  color: #51515C;
}

.max-w-1200 {
  @apply mx-auto;
  max-width: 119.2rem;
}

.max-w-600 {
  @apply mx-auto;
  max-width: 60rem;
}

.max-w-800 {
  @apply mx-auto;
  max-width: 86rem;
}

.max-w-667 {
  @apply mx-auto;
  max-width: 66.7rem;
}

.horizontal-scroll-center {
  min-width: 21.2rem;
}

.dont-worry-usps {
  @apply bg-transparent p-0 text-left;
}

.new-gray {
  color: #4B5563;
}

>>> .max-w-51rem {
  max-width: unset;
}

>>> .faq {
  @apply bg-women-shade border-women-shade;

  .faq-body, .faq-description {
    @apply bg-women-shade border-women-shade rounded;
  }
}

.what-you-get-usps {
  @apply px-0 !important;

  >>> .ota-usps-container {
    @apply p-0 bg-women-shade;

    svg {
      @apply w-4 h-4 fill-green-400;
    }

    span {
      @apply text-women-text !important;
    }

    &-grid {
      @apply block;
      max-width: 32rem;
    }
  }
}

@screen md {
   .max-w-408 {
    @apply absolute bg-transparent top-0 bottom-0 flex flex-col justify-center;
    max-width: 40.8rem;
  }

  .oimage >>> .hero-header {
    min-height: unset;
  }

  .dont-worry-usps {
    @apply px-10;
  }

  .hero-header-container {
    max-width: unset;
  }

  .max-w-56rem {
    @apply mx-auto;
    max-width: 56.4rem;
  }

  .left-flex {
    flex: 60;
  }

  .right-flex {
    @apply px-10;
    flex: 40;
  }

  .o-heading-1 {
    line-height: normal;
  }
}

@screen lg {
  .left-flex {
    flex: 70;
  }

  .right-flex {
    flex: 30;
  }

  .oimage >>> .hero-header {
    min-height: 60rem;
  }
}

/* Add visual indication of focus */
.horizontal-scroll:focus {
  outline: 2px solid #36C0B6; /* Use your primary color */
  outline-offset: -2px;
}

/* Add scrollbar styling for visual users */
.horizontal-scroll {
  scroll-behavior: smooth; /* For smooth scrolling */
}
</style>
